import { DateTime } from 'luxon';
import { MedicalDataTypes } from '../primary-record-types';

/** Hard safety cap — an inclusive 12-month daily series spans up to 367 days. */
export const MAX_OCCURRENCES = 367;
export const MAX_SERIES_MONTHS = 12;
export const DEFAULT_SERIES_MONTHS = 3;

export type ExpandableVisit = Omit<MedicalDataTypes.PrimaryRecordVisit, 'id'> & { id?: string };

/**
 * Materializes every occurrence of a recurring visit series. Each occurrence keeps the
 * base visit's time-of-day (start and end) and carries the shared recurrenceGroupId and
 * rule. The base visit's own date is included when it matches the rule.
 */
export const expandVisitOccurrences = <TVisit extends ExpandableVisit>(
    baseVisit: TVisit,
    recurrence: MedicalDataTypes.PrimaryRecordVisitRecurrence,
    recurrenceGroupId: string,
): TVisit[] => {
    if (!baseVisit.startDateTime) return [];

    const start = DateTime.fromISO(baseVisit.startDateTime);
    const end = baseVisit.endDateTime ? DateTime.fromISO(baseVisit.endDateTime) : null;
    const lastDay = DateTime.fromISO(recurrence.endDate).endOf('day');
    if (!start.isValid || !lastDay.isValid || lastDay < start) return [];

    // luxon weekday is 1 (Monday) - 7 (Sunday); the rule uses 0 (Sunday) - 6 (Saturday).
    const ruleWeekdays =
        recurrence.frequency === 'weekly'
            ? recurrence.weekdays?.length
                ? recurrence.weekdays
                : [start.weekday % 7]
            : null;

    const occurrences: TVisit[] = [];
    for (
        let day = start.startOf('day');
        day <= lastDay && occurrences.length < MAX_OCCURRENCES;
        day = day.plus({ days: 1 })
    ) {
        if (ruleWeekdays && !ruleWeekdays.includes(day.weekday % 7)) continue;

        const occurrenceStart = day.set({ hour: start.hour, minute: start.minute });
        occurrences.push({
            ...baseVisit,
            startDateTime: occurrenceStart.toISO() as string,
            endDateTime: end ? day.set({ hour: end.hour, minute: end.minute }).toISO() ?? undefined : undefined,
            recurrenceGroupId,
            recurrence,
        });
    }
    return occurrences;
};

/** endDate must land within MAX_SERIES_MONTHS of the visit's start. */
export const isRecurrenceEndDateValid = (startDateTime: string, endDate: string): boolean => {
    const start = DateTime.fromISO(startDateTime);
    const end = DateTime.fromISO(endDate);
    if (!start.isValid || !end.isValid) return false;
    return end >= start.startOf('day') && end <= start.plus({ months: MAX_SERIES_MONTHS });
};

export const getDefaultRecurrenceEndDate = (startDateTime: string): string =>
    DateTime.fromISO(startDateTime).plus({ months: DEFAULT_SERIES_MONTHS }).toFormat('yyyy-MM-dd');
